import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, FlaskConical, Trash2, LogOut } from "lucide-react";
import { Header } from "../components/Header";
import { OptionChainTable } from "../components/OptionChainTable";
import { RollRecommendation } from "../components/RollRecommendation";
import { PayoffChart } from "../components/PayoffChart";
import { MiniHeatmap } from "../components/MiniHeatmap";
import { BacktestPositions } from "../components/BacktestPositions";
import {
  btMeta, btListSessions, btCreateSession, btGetSession, btDeleteSession,
  btStep, btChain, btHeatmap, btAnalyze, btOpenPosition, btRoll, btClose,
} from "../lib/api";

const STEP_OPTIONS = [
  { label: "1 hour", minutes: 60 },
  { label: "3 hours", minutes: 180 },
  { label: "6 hours", minutes: 360 },
  { label: "1 day", minutes: 1440 },
];

const fmtSimTs = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { weekday: "short", month: "short", day: "2-digit" })} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

export default function BacktestPage() {
  const [meta, setMeta] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [state, setState] = useState(null); // { session, snapshots }
  const [selectedPosId, setSelectedPosId] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [expFilter, setExpFilter] = useState("ALL");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    btMeta().then(setMeta).catch((e) => toast.error(e?.response?.data?.detail || "Recorded-data source unavailable"));
    btListSessions().then(setSessions).catch(() => {});
  }, []);

  const session = state?.session;

  const refreshHeatmap = useCallback(async (sess, posId) => {
    if (!posId || !sess?.positions?.some((p) => p.id === posId)) {
      setHeatmap(null);
      setAnalysis(null);
      return;
    }
    try {
      const h = await btHeatmap(sess.id, posId);
      setHeatmap(h);
    } catch (e) {
      setHeatmap(null);
      toast.error(e?.response?.data?.detail || "Heatmap failed");
    }
  }, []);

  const applyState = useCallback(async (st, posId = selectedPosId) => {
    setState(st);
    setAnalysis(null);
    const validPos = st.session.positions.some((p) => p.id === posId) ? posId : st.session.positions[0]?.id ?? null;
    setSelectedPosId(validPos);
    await refreshHeatmap(st.session, validPos);
  }, [selectedPosId, refreshHeatmap]);

  const resume = async (id) => {
    setBusy(true);
    try { await applyState(await btGetSession(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load session"); }
    finally { setBusy(false); }
  };

  const create = async (body) => {
    setBusy(true);
    try {
      const st = await btCreateSession(body);
      if (Object.keys(st.session.skipped_symbols || {}).length) {
        toast.warning(`Skipped (no data): ${Object.keys(st.session.skipped_symbols).join(", ")}`);
      }
      await applyState(st, null);
      btListSessions().then(setSessions).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create session");
    } finally { setBusy(false); }
  };

  const doStep = async (direction) => {
    if (!session) return;
    setBusy(true);
    try { await applyState(await btStep(session.id, { direction })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Step failed"); }
    finally { setBusy(false); }
  };

  const setStepMinutes = async (minutes) => {
    if (!session) return;
    setState((s) => ({ ...s, session: { ...s.session, step_minutes: minutes } }));
    try { await btStep(session.id, { direction: 0, step_minutes: minutes }); } catch {}
  };

  const onSelectCandidate = async (cand) => {
    if (!session || !selectedPosId) return;
    try {
      setAnalysis(await btAnalyze(session.id, { position_id: selectedPosId, new_strike: cand.strike, new_expiration: cand.expiration }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyze failed"); }
  };

  const onExecuteRoll = async ({ keep_old }) => {
    if (!session || !selectedPosId || !analysis) return;
    try {
      const st = await btRoll(session.id, selectedPosId, {
        new_strike: analysis.new_option.strike,
        new_expiration: analysis.new_option.expiration,
        keep_old,
      });
      toast.success(`Paper roll ${st.session.events[st.session.events.length - 1].net_credit >= 0 ? "credit" : "debit"} logged`);
      await applyState(st, st.new_position_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Roll failed"); }
  };

  const onOpen = async (body) => {
    try {
      const st = await btOpenPosition(session.id, body);
      toast.success(`Paper call sold: ${body.symbol} $${body.strike}C`);
      await applyState(st, st.session.positions[st.session.positions.length - 1]?.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Open failed"); }
  };

  const onClosePos = async (pid) => {
    try {
      const st = await btClose(session.id, pid);
      toast.success("Paper position closed (bought back)");
      await applyState(st, selectedPosId === pid ? null : selectedPosId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };

  const deleteSession = async (id) => {
    try {
      await btDeleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (session?.id === id) { setState(null); setHeatmap(null); setAnalysis(null); }
      toast.success("Session deleted");
    } catch { toast.error("Delete failed"); }
  };

  const selectedPosition = session?.positions.find((p) => p.id === selectedPosId);
  const currentCandidate = analysis?.new_option;

  return (
    <div className="App flex flex-col h-screen">
      <Header />
      <main className="flex-1 min-h-0 overflow-y-auto max-w-[1600px] w-full mx-auto px-4 md:px-6 pt-3 pb-3 space-y-3">
        <div className="flex items-center justify-between" data-testid="bt-page-header">
          <div>
            <div className="label-cap flex items-center gap-2"><FlaskConical size={12} /> Backtest / Replay</div>
            <p className="text-[11px] text-[#71717A] mt-1">Paper-trade against locally recorded option chains.</p>
          </div>
          <Link to="/" className="btn-outline flex items-center gap-1.5" data-testid="bt-back-to-dashboard">
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>
        {!session && (
          <SetupView meta={meta} sessions={sessions} onCreate={create} onResume={resume} onDelete={deleteSession} busy={busy} />
        )}

        {session && (
          <>
            <section className="border border-[#BF5AF2] bg-[rgba(191,90,242,0.05)] px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="bt-control-bar">
              <div className="flex items-center gap-2 text-[#BF5AF2] text-xs font-bold tracking-widest uppercase">
                <FlaskConical size={14} /> Replay
              </div>
              <div className="flex items-center gap-1.5">
                <button data-testid="bt-step-back" onClick={() => doStep(-1)} disabled={busy} className="btn-outline px-2 py-1.5 disabled:opacity-40"><ChevronLeft size={14} /></button>
                <div className="font-mono text-sm text-white min-w-[190px] text-center" data-testid="bt-sim-clock">{fmtSimTs(session.current_ts)}</div>
                <button data-testid="bt-step-forward" onClick={() => doStep(1)} disabled={busy} className="btn-outline px-2 py-1.5 disabled:opacity-40"><ChevronRight size={14} /></button>
              </div>
              <select
                data-testid="bt-step-resolution"
                value={session.step_minutes}
                onChange={(e) => setStepMinutes(Number(e.target.value))}
                className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1.5 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none"
              >
                {STEP_OPTIONS.map((o) => <option key={o.minutes} value={o.minutes}>step: {o.label}</option>)}
              </select>
              <div className="flex items-center gap-3 font-mono text-[11px]">
                {session.symbols.map((s) => {
                  const snap = state.snapshots[s];
                  return (
                    <span key={s} data-testid={`bt-price-${s}`}>
                      <span className="text-[#A1A1AA]">{s}</span>{" "}
                      {snap?.underlying_price != null
                        ? <span className="text-white">${snap.underlying_price.toFixed(2)}</span>
                        : <span className="text-[#FF3B30]">n/a</span>}
                    </span>
                  );
                })}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <span className={`font-mono text-xs ${session.total_credit >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`} data-testid="bt-total-credit">
                  Σ {session.total_credit >= 0 ? "+" : "−"}${Math.abs(session.total_credit).toFixed(2)}/sh
                </span>
                <button data-testid="bt-exit-session" onClick={() => { setState(null); setHeatmap(null); setAnalysis(null); btListSessions().then(setSessions).catch(() => {}); }} className="btn-outline text-xs py-1.5 flex items-center gap-1"><LogOut size={12} /> Exit</button>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <aside className="lg:col-span-3 space-y-4">
                <BacktestPositions
                  session={session}
                  loadChain={(sym) => btChain(session.id, sym)}
                  selectedId={selectedPosId}
                  onSelect={async (id) => { setSelectedPosId(id); setAnalysis(null); await refreshHeatmap(session, id); }}
                  onOpen={onOpen}
                  onClose={onClosePos}
                />
                <EventLog events={session.events} />
              </aside>

              <section className="lg:col-span-6 space-y-4">
                <OptionChainTable
                  heatmap={heatmap}
                  onSelectCandidate={onSelectCandidate}
                  selectedCandidate={currentCandidate}
                  expFilter={expFilter}
                  setExpFilter={setExpFilter}
                />
                <PayoffChart
                  position={selectedPosition}
                  underlying={heatmap?.underlying_price}
                  currentOption={heatmap?.current_option}
                  newOption={currentCandidate}
                  netCredit={analysis?.metrics?.net_credit}
                />
              </section>

              <aside className="lg:col-span-3 flex flex-col gap-4">
                <RollRecommendation analysis={analysis} onExecuteRoll={onExecuteRoll} onClose={() => setAnalysis(null)} />
                {heatmap?.candidates?.length > 0 && (
                  <MiniHeatmap
                    candidates={heatmap.candidates}
                    underlying={heatmap.underlying_price}
                    position={heatmap.position}
                    selectedCandidate={currentCandidate}
                    onPick={onSelectCandidate}
                    expFilter={expFilter}
                    setExpFilter={setExpFilter}
                  />
                )}
              </aside>
            </div>
          </>
        )}
        {busy && (
          <div className="fixed bottom-4 right-4 text-[11px] font-mono text-[#A1A1AA] bg-[#121212] border border-[#27272A] px-3 py-1.5">
            STEPPING…
          </div>
        )}
      </main>
    </div>
  );
}

const SetupView = ({ meta, sessions, onCreate, onResume, onDelete, busy }) => {
  const [selected, setSelected] = useState([]);
  const [startTs, setStartTs] = useState("");
  const [stepMinutes, setStepMinutes] = useState(180);

  useEffect(() => {
    if (!meta?.symbols?.length || startTs) return;
    const minFirst = meta.symbols.map((s) => s.first_ts).sort()[0];
    if (minFirst) setStartTs(minFirst.slice(0, 16));
  }, [meta, startTs]);

  const toggle = (sym) =>
    setSelected((prev) => (prev.includes(sym) ? prev.filter((s) => s !== sym) : [...prev, sym]));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bt-setup">
      <div className="panel">
        <div className="panel-h"><div className="label-cap">New Backtest Session</div></div>
        <div className="p-4 space-y-4">
          <div>
            <div className="label-cap mb-2">Symbols (recorded coverage)</div>
            {!meta && <p className="text-[11px] text-[#71717A]">Loading recorded coverage…</p>}
            <div className="space-y-1">
              {(meta?.symbols || []).map((s) => (
                <label key={s.symbol} data-testid={`bt-setup-symbol-${s.symbol}`} className="flex items-center gap-2 text-xs font-mono cursor-pointer hover:bg-[#121212] px-2 py-1 -mx-2">
                  <input type="checkbox" checked={selected.includes(s.symbol)} onChange={() => toggle(s.symbol)} className="accent-[#BF5AF2]" />
                  <span className="text-white w-14">{s.symbol}</span>
                  <span className="text-[#71717A] text-[10px]">
                    {new Date(s.first_ts).toLocaleDateString()} → {new Date(s.last_ts).toLocaleDateString()} · {s.snapshot_count ?? "?"} snaps · ~{s.approx_resolution_minutes ? Math.round(s.approx_resolution_minutes) : "?"}min
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="label-cap mb-1">Start (UTC)</div>
            <input data-testid="bt-setup-start" type="datetime-local" value={startTs} onChange={(e) => setStartTs(e.target.value)} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none" />
          </div>
          <div>
            <div className="label-cap mb-1">Step Resolution</div>
            <select data-testid="bt-setup-resolution" value={stepMinutes} onChange={(e) => setStepMinutes(Number(e.target.value))} className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-xs font-mono text-white focus:border-[#BF5AF2] outline-none">
              {STEP_OPTIONS.map((o) => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
            </select>
            <p className="text-[10px] text-[#71717A] mt-1">Steps snap to US market hours (Mon–Fri 9:30–16:00 ET) — recording is ~3h resolution, smaller steps may show the same snapshot.</p>
          </div>
          <button
            data-testid="bt-setup-create"
            onClick={() => onCreate({ symbols: selected, start_ts: startTs.length === 16 ? `${startTs}:00Z` : startTs, step_minutes: stepMinutes })}
            disabled={busy || selected.length === 0 || !startTs}
            className="btn-accent w-full py-2 text-xs disabled:opacity-40"
          >
            Start Replay
          </button>
        </div>
      </div>

      <div className="panel h-fit">
        <div className="panel-h"><div className="label-cap">Saved Sessions</div></div>
        <div className="p-3 space-y-1.5">
          {sessions.length === 0 && <p className="text-[11px] text-[#71717A] py-2">No saved backtest sessions.</p>}
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between border border-[#27272A] px-3 py-2 hover:bg-[#121212]" data-testid={`bt-session-${s.id}`}>
              <button onClick={() => onResume(s.id)} className="text-left flex-1">
                <div className="font-mono text-xs text-white">{s.symbols.join(", ")}</div>
                <div className="text-[10px] text-[#71717A] font-mono">
                  at {fmtSimTs(s.current_ts)} · {s.positions?.length || 0} pos · Σ ${Number(s.total_credit || 0).toFixed(2)}/sh
                </div>
              </button>
              <button data-testid={`bt-delete-session-${s.id}`} onClick={() => onDelete(s.id)} className="text-[#71717A] hover:text-[#FF3B30] p-1.5"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EventLog = ({ events }) => {
  if (!events?.length) return null;
  const rows = [...events].reverse().slice(0, 12);
  return (
    <div className="panel" data-testid="bt-event-log">
      <div className="panel-h"><div className="label-cap">Replay Log</div></div>
      <div className="p-3 space-y-1 font-mono text-[10px]">
        {rows.map((e) => (
          <div key={e.id} className="flex items-baseline justify-between gap-2">
            <span className="text-[#71717A] flex-shrink-0">{new Date(e.ts).toLocaleDateString([], { month: "short", day: "2-digit" })}</span>
            <span className="text-[#A1A1AA] flex-1 truncate">
              {e.type === "create" && `open ${e.symbol} $${e.to_strike}C`}
              {e.type === "roll" && `roll ${e.symbol} $${e.from_strike}→$${e.to_strike}`}
              {e.type === "close" && `close ${e.symbol} $${e.from_strike}C`}
            </span>
            <span className={e.net_credit >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}>
              {e.net_credit >= 0 ? "+" : "−"}${Math.abs(e.net_credit).toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
